import { formatDate, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { combineLatest } from 'rxjs';
import { PlanningFile } from 'src/app/classes/planning-file';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import { ImageModalComponent } from 'src/app/generic/components/image-modal/image-modal.component';
import { FileStorage } from 'src/app/services/file.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { RealisationService } from 'src/app/services/realisation.service';
import { LocalStorage } from 'src/app/storage.class';
import {
    TracklineOrderFormComponent,
    TracklineOrderFormDialogData,
} from 'src/app/trackline/pages/trackline-order-form/trackline-order-form.component';
import { Utils } from 'src/app/utils.class';
import { Material } from '../../../classes/material.class';
import { mergePlanningMaterialPivots, MaterialService } from '../../../services/material/material.service';

@Component({
    selector: 'app-day-detail',
    templateUrl: './day-detail.component.html',
    styleUrls: ['./day-detail.component.scss'],
    standalone: false,
})
export class DayDetailComponent extends CodaltComponent implements OnInit {
    maxDate: Date;
    date: Date;
    nextDate: Date;
    dateString: string;
    realisations: Realisation[];
    notWorkRealisations: Realisation[];

    fileStorage = FileStorage.Planning;
    projectmanager: string;
    performer: string;
    asphalt_performer: string;

    showWorkWith = false;
    showWorkWithFrom: Date;

    planningMaterialMap = new Map<number, Material[]>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private materialService: MaterialService,
        private realisationService: RealisationService,
        private planningService: PlanningService,
        private location: Location
    ) {
        super();

        this.maxDate = this.realisationService.calcMaxDate();
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { date: string }) => {
                this.date = new Date(params.date);
                this.dateString = Utils.dateString(this.date);
                this.nextDate = new Date(this.date);
                this.nextDate.setDate(this.nextDate.getDate() + 1);
                if (this.date < this.maxDate) {
                    this.realisationService.getUserDaySchedule(this.date).subscribe(realisations => {
                        const userRealisations = realisations.data.realisations.filter(r => !!r.user_id);
                        this.realisations = userRealisations.filter(r => r.hourtype === RealisationHourtype.worktime);
                        this.realisations.forEach(r => {
                            this.subscriptions.add(
                                combineLatest([
                                    this.materialService.getForPlanning(r.planning_id),
                                    this.planningService.materials$,
                                ]).subscribe(([pivots, catalog]) => {
                                    this.planningMaterialMap.set(
                                        r.planning_id,
                                        mergePlanningMaterialPivots(catalog, pivots)
                                    );
                                })
                            );
                        });
                        this.notWorkRealisations = userRealisations.filter(r =>
                            [
                                RealisationHourtype.day_off,
                                RealisationHourtype.education,
                                RealisationHourtype.bank_holiday,
                                RealisationHourtype.frost,
                                RealisationHourtype.sleep,
                                RealisationHourtype.illness,
                                RealisationHourtype.travel_to,
                                RealisationHourtype.travel_back,
                                RealisationHourtype.driving_to,
                                RealisationHourtype.driving_back,
                            ].includes(r.hourtype)
                        );
                    });
                    this.showWorkWithFrom = new Date(this.date);
                    do {
                        this.showWorkWithFrom.setDate(this.showWorkWithFrom.getDate() - 1);
                    } while (!Utils.isWorkingday(this.showWorkWithFrom));
                    this.showWorkWith =
                        this.environment.allowWorksWith || this.showWorkWithFrom.getTime() <= new Date().getTime();
                }

                window.scroll(0, 0);
            })
        );
    }

    private getFile(planningFileId: number, cb: (data: Blob) => void) {
        this.subscriptions.add(
            this.planningService.getFile(planningFileId).subscribe(cb, error => {
                console.error('Error downloading file', error);
            })
        );
    }

    openFile(file: PlanningFile) {
        const fileName = file.name?.toLowerCase() ?? '';
        const isPdf = fileName.endsWith('.pdf');
        const isImage = /\.(jpe?g|png|gif|svg)$/.test(fileName);

        if (!isPdf && !isImage) {
            if (file.id) {
                this.downloadFile(file);
            }
            return;
        }

        if (!file.file) {
            return;
        }

        const url = `${this.environment.apiEndpoint}file/planning/${file.file}?access_token=${LocalStorage.getUserToken()}`;

        this.dialog.open(ImageModalComponent, {
            panelClass: 'image-viewer',
            width: '99vw',
            maxWidth: '1300px',
            height: '99vh',
            minHeight: '500px',
            maxHeight: '99vh',
            hasBackdrop: true,
            data: {
                viewIndex: 0,
                ...(isPdf ? { singlePdf: url } : { images: [url] }),
            },
        });
    }

    downloadFile(file: PlanningFile) {
        this.getFile(file.id, data => saveAs(data, file.name));
    }

    openOrderForm(planningId: number) {
        this.dialog.open<TracklineOrderFormComponent, TracklineOrderFormDialogData>(TracklineOrderFormComponent, {
            data: { planningId },
            maxWidth: '1050px',
            width: '100%',
            panelClass: 'order-form-dialog',
        });
    }

    next() {
        this.date.setDate(this.date.getDate() + 1);
        this.router.navigateByUrl(`weekschedule/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}`);
    }

    prev() {
        this.date.setDate(this.date.getDate() - 1);
        this.router.navigateByUrl(`weekschedule/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}`);
    }

    back() {
        // Navigate back to the previous page in the browser history when possible, otherwise navigate to the week schedule for the current date
        if (window.history.length > 1) {
            this.location.back();
        } else {
            const weekNumber = Utils.getWeekNumber(this.date);
            this.router.navigateByUrl(`weekschedule/${weekNumber}/${formatDate(this.date, 'yyyy', 'nl')}`);
        }
    }
}
